import type { FunctionComponent } from "react"

import dynamic from "next/dynamic"
import { useParams } from "next/navigation"

import { Heading } from "@jobteaser/spark/components/Heading"
import { Layout, LAYOUT_COLOR_BLOCK } from "@jobteaser/ui-kit/components/Layout/Layout"

import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { useCompanyInsightsFetch } from "@/modules/community/hooks/useCompanyInsightsFetch"
import { COMPANY_PAGE_TYPE } from "@/modules/companies/client/api/endpoints/company"
import { getQuestions } from "@/modules/companies/client/components/Faq/utils/getQuestions"
import { RECRUITMENT_PATTERN_ID } from "@/modules/companies/client/components/RecruitmentPatternInformation/RecruitmentPatternInformation"
import { useCompanyAnalyticsData } from "@/modules/companies/client/components/RecruitmentPatternInformation/useCompanyAnalyticsData"
import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
import { COMPANY_BASENAME, COMPANY_JOB_OFFERS } from "@/modules/companies/client/utils/routing"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { Link } from "@/modules/routing/components/Link"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

import styles from "./WorkThere.module.css"

const RecruitmentPatternBannerDynamic = dynamic(() =>
  import("@/modules/companies/client/components/RecruitmentPatternBanner/RecruitmentPatternBanner").then(
    mod => mod.RecruitmentPatternBanner
  )
)

const InsightsDynamic = dynamic(
  () => import("@/modules/companies/client/components/Insights/Insights").then(mod => mod.Insights),
  {
    ssr: false,
  }
)

const FaqDynamic = dynamic(() => import("@/modules/companies/client/components/Faq/Faq").then(mod => mod.Faq), {
  ssr: false,
})

const CertificationDynamic = dynamic(
  () => import("@/modules/companies/client/components/Certification/Certification").then(mod => mod.Certification),
  {
    ssr: false,
  }
)

export const WorkThere: FunctionComponent = () => {
  const { t } = useTranslation(["companies_client"])
  const locale = useLocale()
  const { companySlug } = useParams()
  const { dataCurrentUser } = useCurrentUserFetch()
  const { dataCompany } = useCompanyFetch()
  const { dataCareerCenter } = useCareerCenterFetch()
  const { dataCompanyInsights } = useCompanyInsightsFetch(dataCompany?.id || "")
  const { hasEnoughData } = useCompanyAnalyticsData()

  if (!dataCompany) return null

  const {
    pageType,
    id,
    page: { name, faq, glassdoorId, greatPlaceToWorkPermalink, greatPlaceToWorkYear, chooseMyCompany },
  } = dataCompany

  const companyDataTrack = { id, name }

  const faqQuestions = getQuestions(dataCompany, t).filter(question => question.answer.length > 0)

  const isPremiumCompany = pageType === COMPANY_PAGE_TYPE.PREMIUM
  const hasFaqWithAnswers = faqQuestions.length > 0
  const isLastYear = new Date().getFullYear() - 1

  const displayFaq = faq && hasFaqWithAnswers
  const displayInsights =
    dataCurrentUser?.isLogged &&
    !!dataCompanyInsights?.experiencesInfos?.totalExperiences &&
    dataCompanyInsights?.experiencesInfos?.totalExperiences > 0 &&
    !!dataCompanyInsights?.experiencesInfos.experienceToDisplay?.insights.positiveFeedback
  const displayGreatPlaceToWork = !!greatPlaceToWorkPermalink && (greatPlaceToWorkYear || 0) >= isLastYear
  const displayCertification = isPremiumCompany && (displayGreatPlaceToWork || !!chooseMyCompany || !!glassdoorId)
  const displayRecruitmentPatternBanner = isPremiumCompany && hasEnoughData

  const displayWorkThere = displayFaq || displayInsights || displayCertification || displayRecruitmentPatternBanner

  if (!displayWorkThere) return null

  return (
    <Layout hasBottomPadding colorBlock={LAYOUT_COLOR_BLOCK.WHITE} data-testid="company_information_work_there">
      <Layout.Column>
        <Heading
          className={styles.title}
          data-testid="company_information_work_there_title"
          as="h2"
          variant="title2"
          weight="bold"
        >
          {t("companies_client.information.how.title", { name })}
        </Heading>
        <div className={styles.container}>
          {displayFaq && (
            <div className={styles.content}>
              <FaqDynamic companyDataTrack={companyDataTrack} questions={faqQuestions} className={styles.faq} />
            </div>
          )}
          {(displayInsights || displayCertification) && (
            <div className={styles.content}>
              {displayInsights && (
                <InsightsDynamic
                  careerCenterName={dataCareerCenter?.name}
                  isCareerCenterIntranet={dataCareerCenter?.intranet}
                  companyName={name}
                  experiences={dataCompanyInsights?.experiencesInfos}
                  className={styles.insights}
                />
              )}
              {displayCertification && (
                <CertificationDynamic
                  isGreatPlaceToWork={displayGreatPlaceToWork}
                  isChooseMyCompany={!!chooseMyCompany}
                  isGlassDoor={!!glassdoorId}
                  className={styles.certifications}
                />
              )}
            </div>
          )}
          {displayRecruitmentPatternBanner && (
            <div className={styles.content}>
              <RecruitmentPatternBannerDynamic
                companyDataTrack={companyDataTrack}
                className={styles.recruitmentBanner}
                href={`/${locale}/${COMPANY_BASENAME}/${companySlug}/${COMPANY_JOB_OFFERS}#${RECRUITMENT_PATTERN_ID}`}
                linkComponent={Link}
              />
            </div>
          )}
        </div>
      </Layout.Column>
    </Layout>
  )
}
